import { motion } from "framer-motion";
import { Zap, Lock, EyeOff, WifiOff, Apple, Play, Mail } from "lucide-react";
import { RevealText } from "./RevealText";

const PRINCIPLES = [
  {
    icon: Zap,
    title: "Velocità",
    body: "L'aiuto deve essere a un solo gesto di distanza.",
  },
  {
    icon: Lock,
    title: "Privacy by design",
    body: "I tuoi dati appartengono a te, e solo a te.",
  },
  {
    icon: EyeOff,
    title: "Discrezione",
    body: "Gli strumenti di sicurezza non devono esporti a rischi ulteriori.",
  },
  {
    icon: WifiOff,
    title: "Resilienza offline",
    body: "La protezione non può dipendere da una rete perfetta.",
  },
];

export const About = () => {
  return (
    <section
      id="app"
      data-testid="about-section"
      className="relative py-28 sm:py-36 bg-brand-bg"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-14 gap-x-10">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <span className="text-xs uppercase tracking-[0.25em] text-brand-primary">
                Cos'è Safe(H)er
              </span>
              <h2 className="mt-6 font-display font-medium tracking-tight leading-[0.95] text-brand-text text-4xl sm:text-5xl md:text-6xl">
                <RevealText inView lines={["L'aiuto,", "a portata", "di gesto."]} />
              </h2>
              <p className="mt-8 text-brand-muted text-base md:text-lg leading-relaxed max-w-md">
                Safe(H)er è un'app mobile per la sicurezza personale pensata per
                aiutare chi è in pericolo — incluse le persone sopravvissute a
                violenza domestica, stalking, molestie e aggressioni — a
                raggiungere la propria rete fidata e i servizi di emergenza il
                più velocemente possibile.
              </p>
              <p className="mt-5 text-brand-muted/80 text-sm leading-relaxed max-w-md italic font-serif-accent">
                Non pubblichiamo intenzionalmente i dettagli implementativi
                dell'app, per la sicurezza di chi la usa.
              </p>

              <div className="mt-9 rounded-3xl border border-brand-line bg-brand-surface p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-brand-muted">
                  Hai domande o vuoi collaborare?
                </p>
                <a
                  href="mailto:safherofficial@gmail.com"
                  data-testid="about-email-btn"
                  className="group mt-4 inline-flex items-center gap-2.5 rounded-full bg-brand-primary text-brand-inverse px-6 py-3.5 hover:bg-brand-primaryhover transition-colors duration-300 active:scale-95"
                >
                  <Mail size={18} className="shrink-0" />
                  Scrivici
                </a>
                <p className="mt-3 text-sm text-brand-muted break-all">
                  safherofficial@gmail.com
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-6">
              I nostri principi di design
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PRINCIPLES.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  className="rounded-3xl border border-brand-line bg-brand-surface p-7"
                  data-testid={`principle-${i}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-primary/10 text-brand-primary">
                    <p.icon size={22} />
                  </div>
                  <h3 className="mt-5 font-display text-2xl text-brand-text tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2.5 text-brand-muted leading-relaxed text-sm">
                    {p.body}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Availability */}
            <div className="mt-6 rounded-3xl border border-brand-line bg-brand-dark p-7 sm:p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-inverse/50">
                Disponibilità
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-4">
                <div className="flex-1 flex items-start gap-4 rounded-2xl bg-brand-inverse/5 p-5">
                  <Play size={22} className="text-brand-inverse mt-0.5 shrink-0" />
                  <div>
                    <p className="font-display text-xl text-brand-inverse">
                      Android
                    </p>
                    <p className="text-sm text-brand-inverse/60 mt-1">
                      Disponibile su Google Play (in rilascio graduale).
                    </p>
                  </div>
                </div>
                <div className="flex-1 flex items-start gap-4 rounded-2xl bg-brand-inverse/5 p-5">
                  <Apple size={22} className="text-brand-inverse mt-0.5 shrink-0" />
                  <div>
                    <p className="font-display text-xl text-brand-inverse">
                      iOS
                    </p>
                    <p className="text-sm text-brand-inverse/60 mt-1">
                      Presto disponibile.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
